import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  HostBinding,
  InjectionToken,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import { RadioComponent } from '../radio/radio';

export const AFP_RADIO_GROUP = new InjectionToken<RadioGroupComponent>('AFP_RADIO_GROUP');

let uniqueGroupId = 0;

@Component({
  selector: 'afp-radio-group',
  templateUrl: './radio-group.html',
  styleUrls: ['./radio-group.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => RadioGroupComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => RadioGroupComponent), multi: true },
    { provide: AFP_RADIO_GROUP, useExisting: forwardRef(() => RadioGroupComponent) }
  ]
})
export class RadioGroupComponent implements ControlValueAccessor, AfterContentInit, OnDestroy, OnChanges, Validator {
  @Input() name = `afp-radio-group-${uniqueGroupId++}`;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() orientation: 'vertical' | 'horizontal' = 'vertical';

  @Output() change = new EventEmitter<any>();

  private _value: any = null;
  get value(): any {
    return this._value;
  }

  @HostBinding('attr.orientation') get hostOrientation() {
    return this.orientation;
  }

  @ContentChildren(RadioComponent, { descendants: true })
  radios!: QueryList<RadioComponent>;

  private radioButtonSubscriptions = new Subscription();
  private onChange: (val: any) => void = () => {};
  private onTouch: () => void = () => {};
  private validatorChange?: () => void;

  writeValue(val: any): void {
    this._value = val;
  }

  registerOnChange(onChange: (val: any) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouch: () => void): void {
    this.onTouch = onTouch;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.required) return null;
    const value = control.value;
    const has = !(value === null || value === undefined || value === '');
    return has ? null : { required: true };
  }

  registerOnValidatorChange(validationChange: () => void): void {
    this.validatorChange = validationChange;
  }

  ngOnChanges(ch: SimpleChanges): void {
    if ('required' in ch) this.validatorChange?.();
  }

  ngAfterContentInit(): void {
    this.initRadios();
    this.radioButtonSubscriptions.add(this.radios.changes.subscribe(() => this.initRadios()));
  }

  ngOnDestroy(): void {
    this.radioButtonSubscriptions.unsubscribe();
  }

  private initRadios(): void {
    this.radioButtonSubscriptions.unsubscribe();
    this.radioButtonSubscriptions = new Subscription();
    this.radioButtonSubscriptions.add(this.radios.changes.subscribe(() => this.initRadios()));

    this.radios.forEach(radio => {
      this.radioButtonSubscriptions.add(
        radio.change.subscribe(selectedValue => {
          if (this.disabled) return;
          if (this._value === selectedValue) {
            this.onTouch();
            return;
          }
          this._value = selectedValue;
          this.onChange(this._value);
          this.onTouch();
          this.change.emit(this._value);
        })
      );
    });
  }
}
